import React from 'react';
import PropTypes from 'prop-types';
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import { Config, BUILD_TYPE } from 'utils/webappConfig';
import { getLogger } from 'utils/logger';
import { getState } from 'utils/getStore';
import * as profileSelectors from 'data/profile/selectors';
import { getDatasetId } from 'utils/auth/selectors';
import tracker from 'utils/tracker';
import { getDisableBugsnag, getIgnoreBugsnagReport } from 'utils/localPreferences';

const bugsnagDisabled = getDisableBugsnag();
const ignoreBugsnagReport = getIgnoreBugsnagReport();

let showErrorPageTimer;
const logger = getLogger('[bugsnag]');
const suppressErrorPageErrors = [
  'NetworkError',
  'UnhandledRejection',
  'HTTPError',
];

export const bugsnagClient = bugsnagDisabled ? { notify: () => {} } : Bugsnag;
if (!bugsnagDisabled) {
  Bugsnag.start({
    apiKey: Config.builds[BUILD_TYPE]?.bugsnag_api_key, // eslint-disable-line camelcase
    // appType: 'client',
    appVersion: String(process.env.APP_VERSION),
    autoDetectErrors: true,
    autoTrackSessions: true,
    collectUserIp: true,
    logger,
    enabledBreadcrumbTypes: [
      'error',
      'navigation',
      'request',
      'user',
      // 'log', // sorry no logs until sanitize them
    ],
    maxBreadcrumbs: 20,
    maxEvents: 3,
    plugins: [new BugsnagPluginReact(React)],
    releaseStage: Config.builds[BUILD_TYPE]?.bugsnag_release_stage, // eslint-disable-line camelcase
    onError: (event) => {
      const sendReport = !ignoreBugsnagReport;
      logger.warn(sendReport ? 'report will be sent\n' : 'report ignored\n', event);

      const state = getState();
      const profile = profileSelectors.getProfile(state);

      const newEvent = event;
      newEvent.setUser(profile && profile.userId);
      newEvent.addMetadata('user', {
        datasetId: getDatasetId(state),
      });

      const {errorClass, errorMessage, stacktrace} = event?.errors?.[0];

      const errorDetails = JSON.stringify(stacktrace?.[0]);
      const props = {
        href: window.location.href,
        path: window.location.pathname,
        exception_name: errorMessage,
        exception_reason: errorDetails,
      };
      tracker.track(tracker.events.exception, props);

      assert(errorClass !== 'UnhandledRejection', 'Top crasher: UnhandledRejection\n%s', JSON.stringify(event, null, 2));

      if (!errorClass || !suppressErrorPageErrors.includes(errorClass)) {
        clearTimeout(showErrorPageTimer);
        showErrorPageTimer = setTimeout(() => {
          tracker.track(tracker.events.crash, props);

          let showErrorPage = true;

/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////
///////////
///////////////////

          if (showErrorPage) {
            window.location.href = '/error';
          }
        }, 1200);
      }

      return sendReport;
    },
  });
}

class ErrorBoundaryStub extends React.PureComponent {
  static getDerivedStateFromError(_error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  render() {
    if (this.state.hasError) {
      return this.props.FallbackComponent?.() || null;
    }
    return this.props.children;
  }
}

ErrorBoundaryStub.propTypes = {
  FallbackComponent: PropTypes.func,
  children: PropTypes.any,
};

ErrorBoundaryStub.whyDidYouRender = false;

export const ErrorBoundaryBugSnag = bugsnagDisabled ? ErrorBoundaryStub : Bugsnag.getPlugin('react');
export const dismissErrorPage = () => clearTimeout(showErrorPageTimer);
export const bugsnagNotify = (error, onError, ...others) => {
  bugsnagClient.notify(error, onError, ...others);
  dismissErrorPage();
};
export default bugsnagClient;
